/*
 * Copyright (c) 2025-present TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 */

import { fetchMetaobjects, transformNodeToObject } from '@theme/graphql';

class BrandsOverview extends HTMLElement {
  connectedCallback() {
    this.renderBrandsOverview().then();
  }

  /**
   * Get brands entries from Shopify
   *
   * @returns {Promise<({}|*)[] | void>}
   */
  async getBrandEntries() {
    return fetchMetaobjects('brand')
      .then((metaobjects) => metaobjects.map(transformNodeToObject))
      .catch(() => {
        return [];
      });
  }

  /**
   * Get collection url
   *
   * @param {object} collection
   * @returns {string|null}
   */
  getCollectionUrl(collection) {
    const collectionHandle = collection?.handle;
    if (!collectionHandle) {
      return null;
    }
    return `${(this.dataset.rootUrl || '/').replace(/\/$/, '')}/collections/${collectionHandle}`;
  }

  /**
   * Generate the brands overview HTML
   *
   * @param {Array} rawBrands
   * @returns {string}
   */
  generateBrandsOverviewHtml(rawBrands) {
    // filter by brands that have a collection assigned, sort by label
    const brands = rawBrands
      .filter((item) => item.collection)
      .sort((a, b) => a.label.localeCompare(b.label));

    // check if we have brands
    if (!brands.length) {
      return this.dataset.tError;
    }

    // get top brands
    const topBrands = brands.filter(item => item.is_top === 'true');

    // group brands by first letter
    const grouped = brands.reduce((acc, entry) => {
      const firstChar = entry.label[0].toUpperCase();
      const key =
        firstChar >= 'A' && firstChar <= 'Z'
          ? firstChar
          : firstChar >= '0' && firstChar <= '9'
            ? '0-9'
            : '+';

      if (!acc[key]) acc[key] = [];
      acc[key].push(entry);
      return acc;
    }, {});

    // sort keys: A-Z first, then 0-9, then +
    const sortedLetters = Object.keys(grouped).sort((a, b) => {
      if (a === '+') return 2;
      if (b === '+') return -2;
      if (a === '0-9') return 1;
      if (b === '0-9') return -1;
      return a.localeCompare(b);
    });

    // template for a single top brand
    // noinspection JSUnresolvedReference
    const topBrandTemplate = (item) => `
    <li class="top-brand">
      <a href="${this.getCollectionUrl(item?.collection) || '#'}">
        <img src="${item?.logo?.image?.url || item?.collection?.image?.url || ''}" alt="${item?.label || ''}" />
      </a>
    </li>
  `;

    // template for a single brand entry
    // noinspection JSUnresolvedReference
    const brandItemTemplate = (item) => `
    <li>
      <a href="${this.getCollectionUrl(item?.collection) || '#'}">${item?.label || ''}</a>
      ${item?.is_new === 'true' ? `<div class="badge">${this.dataset.tBadgeNew}</div>` : ''}
    </li>
  `;

    // template for a group
    const groupTemplate = (letter, entries) => `
    <h2 id="brands-${letter}">${letter}</h2>
    <ol class="brand-group">
      ${entries.map(brandItemTemplate).join('\n')}
    </ol>
  `;

    // navigation template
    const navTemplate = (letters) => `
    <ol class="brand-nav">
      ${letters.map(letter => `<li><a href="#brands-${letter}">${letter}</a></li>`).join('\n')}
    </ol>
  `;

    // top brands section
    const topBrandsSection = topBrands.length > 0 ? `
    <ol class="top-brands">
      ${topBrands.map(topBrandTemplate).join('\n')}
    </ol>
  ` : '';

    // generate final HTML
    return topBrandsSection +
      navTemplate(sortedLetters) +
      sortedLetters.map(letter => groupTemplate(letter, grouped[letter])).join('');
  }

  /**
   * Generate dummy data as placeholders
   *
   * @returns {[]}
   */
  generateDummyBrands() {
    const letters = [
      ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
      '0-9',
      '+',
    ];

    const dummyBrands = [];

    // 24 top brands
    for (let i = 0; i < 24; i++) {
      dummyBrands.push({
        label: `TopBrand ${i + 1}`,
        collection: { handle: `topbrand-${i + 1}` },
        is_top: "true",
      });
    }

    // 4 brands per letter group
    letters.forEach((letter) => {
      for (let j = 0; j < 4; j++) {
        dummyBrands.push({
          label: `${letter} Brand ${j + 1}`,
          collection: { handle: `${letter.toLowerCase()}-brand-${j + 1}` },
        });
      }
    });

    return dummyBrands;
  }

  /**
   * Render brand overview
   */
  renderBrandsOverview() {
    this.innerHTML = this.generateBrandsOverviewHtml(this.generateDummyBrands());

    return this.getBrandEntries().then((brands) => {
      this.innerHTML = this.generateBrandsOverviewHtml(brands);
      this.setAttribute('data-loaded', 'true');
    });
  }
}

if (!customElements.get('brands-overview')) {
  customElements.define('brands-overview', BrandsOverview);
}
